import { buildHref } from "@softmax/web-addresses";
import { backendFetch } from "@softmax/observatory-client/transport";
import type { Schemas } from "@softmax/observatory-client/types";
import { readAccountProfile } from "@/features/account/records";
import { currentTosVersion } from "@/lib/tos";
import { getAuthSession } from "../session/session";
import {
  getCliCallbackState,
  type cliSearchSchema,
  type cliIssueSchema,
} from "../identity-search";
import type { z } from "zod";
import { type ReadOptions } from "../identity-request";

export async function getCliAuth({
  data,
  signal,
}: ReadOptions<z.infer<typeof cliSearchSchema>>) {
  const callbackState = getCliCallbackState(data.callback);

  if (callbackState.hasInvalidCallback)
    return { kind: "invalid-callback" as const };

  const session = await getAuthSession({ signal });

  if (!session)
    return { kind: "anonymous" as const, callback: callbackState.callback };

  const { user } = await readAccountProfile({
    viewerId: session.user.id,
    signal,
  });

  if (!user.profileCompleted || user.tosVersion !== currentTosVersion) {
    return {
      kind: "profile" as const,
      callback: callbackState.callback,
      sessionUserId: session.user.id,
      user: {
        ...user,
        name: user.name ?? session.user.name ?? "",
        email: user.email ?? session.user.email ?? "",
        institution: user.institution ?? "",
      },
    };
  }

  return {
    kind: "ready" as const,
    callback: callbackState.callback,
    sessionUserId: session.user.id,
    accountLabel: user.email ?? user.name ?? "your Softmax account",
  };
}

export async function createCliAuthCode({
  data,
  signal,
}: ReadOptions<z.infer<typeof cliIssueSchema>>) {
  const admission = await getCliAuth({
    data: { callback: data.callback },
    signal,
  });

  if (admission.kind === "invalid-callback")
    throw new Error("The CLI callback is not allowed.");

  const search = new URLSearchParams();

  if (data.callback) search.set("callback", data.callback);

  const returnHref = buildHref({ name: "cliAuth", search });

  if (admission.kind !== "ready") {
    return {
      kind: "redirect" as const,
      href: buildHref({
        name: admission.kind === "anonymous" ? "signIn" : "completeProfile",
        search: new URLSearchParams({ redirect_url: returnHref }),
      }),
    };
  }

  if (admission.sessionUserId !== data.sessionUserId)
    return { kind: "redirect" as const, href: returnHref };

  const { code } = await backendFetch<Schemas["AuthCodeCreateResponse"]>(
    "",
    "/api/agent-sign-in-code",
    {
      method: "POST",
      headers: { "X-Account-User-Id": data.sessionUserId },
      signal: signal ?? null,
    },
  );
  return {
    kind: "issued" as const,
    code,
    callback: admission.callback,
    loginServer: new URL("/api", window.location.origin).toString(),
  };
}

export function getCliCompletion({
  data,
}: ReadOptions<z.infer<typeof cliSearchSchema>>) {
  return {
    ...getCliCallbackState(data.callback),
    loginServer: new URL("/api", window.location.origin).toString(),
  };
}
